import { PlusOutlined } from "@ant-design/icons";
import Search from "antd/es/input/Search";
import type { MessageInstance } from "antd/es/message/interface";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getExportExportSchedulesThunk } from "tm-shared/api";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { PageBlock } from "@/shared/pageComposition/BlockList";
import { FloatingActionButton } from "@/shared/ui/FloatingActionButton";
import { clearCurrentScheduleList } from "@/store/slices/manager/manager.slice";

import ExportDropdown from "./components/ExportDropdown";
import RouteCard from "./components/RouteCard";
import { RouteManagementCapabilities } from "./RouteManagement.policy";

export type RouteManagementModel = {
    capabilities: RouteManagementCapabilities;
    isLoading: boolean;
    isOffline: boolean;
    messageApi: MessageInstance;
};

export const RouteManagementHeader = ({
    isLoading,
    messageApi,
    canExportRoutes,
}: {
    isLoading: boolean;
    messageApi: MessageInstance;
    canExportRoutes: boolean;
}) => {
    const dispatch = useAppDispatch();
    const exportIndicator = useAppSelector(
        (state) => state.work.exportIndicator,
    );
    const routeList = useAppSelector((state) => state.manager.routeList);

    const handleExportAllRoutes = useCallback(
        async ({ key }: { key: string }) => {
            switch (key) {
                case "excel": {
                    const routeIds = Object.values(routeList).map(
                        (route) => route.uuid,
                    );
                    if (routeIds.length > 0) {
                        await dispatch(
                            getExportExportSchedulesThunk({ routeIds }),
                        );
                        messageApi.info(
                            `Експорт усіх маршрутів торгових агентів розпочато`,
                        );
                    } else {
                        messageApi.warning("Немає маршрутів для експорту");
                    }
                    break;
                }
            }
        },
        [dispatch, messageApi, routeList],
    );

    return (
        <Header
            title={"Редагування маршрутів"}
            titleLoading={isLoading}
            rightActions={
                canExportRoutes ? (
                    <ExportDropdown
                        isLoading={!!exportIndicator}
                        loadingLabel={
                            exportIndicator ? exportIndicator.toString() : ""
                        }
                        menuItems={[{ key: "excel", label: "Експорт в Excel" }]}
                        onMenuClick={handleExportAllRoutes}
                    />
                ) : undefined
            }
        />
    );
};

const RouteListBlock = ({
    messageApi,
    canExportRoutes,
}: {
    messageApi: MessageInstance;
    canExportRoutes: boolean;
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const offlineMode = useOfflineModeSelector();
    const exportIndicator = useAppSelector(
        (state) => state.work.exportIndicator,
    );
    const routeList = useAppSelector((state) => state.manager.routeList);
    const [searchTerm, setSearchTerm] = useState("");

    const handleExportSingleRoute = useCallback(
        async (routeId: string, routeName: string) => {
            await dispatch(
                getExportExportSchedulesThunk({ routeIds: [routeId] }),
            );
            messageApi.info(`Експорт маршруту "${routeName}" розпочато`);
        },
        [dispatch, messageApi],
    );

    const handleNavigateToRoute = useCallback(
        (route: string, routeId: string) => {
            if (offlineMode) return;

            navigate(buildRoutePath(route, { routeId }));
        },
        [navigate, offlineMode],
    );

    const filteredRoutes = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return Object.values(routeList).filter(
            (item) =>
                item.name.toLowerCase().includes(term) ||
                item?.agentInfo?.fullName?.toLowerCase()?.includes(term),
        );
    }, [routeList, searchTerm]);

    return (
        <>
            <Search
                placeholder="Шукати"
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size="large"
                style={{ marginBottom: 16 }}
            />
            {filteredRoutes.map((route) => {
                const { uuid, agentInfo, name, pointCount } = route;

                return (
                    <RouteCard
                        key={uuid}
                        id={uuid}
                        name={name}
                        pointCount={pointCount}
                        agentFullName={agentInfo?.fullName}
                        disableExport={
                            !canExportRoutes || exportIndicator === "A"
                        }
                        onClick={() =>
                            handleNavigateToRoute(
                                ROUTES.RouteManagementEdit,
                                uuid,
                            )
                        }
                        onExportSingleRoute={() =>
                            handleExportSingleRoute(uuid, name)
                        }
                        onViewByDaysOfMonth={() =>
                            handleNavigateToRoute(
                                ROUTES.RouteManagementDaysView,
                                uuid,
                            )
                        }
                    />
                );
            })}
        </>
    );
};

const CreateRouteFabBlock = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const handleCreateNewRoute = useCallback(() => {
        dispatch(clearCurrentScheduleList());
        navigate("new-route");
    }, [dispatch, navigate]);

    return (
        <FloatingActionButton
            position="fixed"
            onClick={handleCreateNewRoute}
            icon={<PlusOutlined />}
            right={16}
            bottom={80}
        />
    );
};

export const getRouteManagementBlocks = (
    model: RouteManagementModel,
): PageBlock<RouteManagementModel>[] => [
    {
        key: "route-management-list",
        order: 10,
        isVisible: (m) => m.capabilities.canViewRoutes,
        render: () => (
            <RouteListBlock
                messageApi={model.messageApi}
                canExportRoutes={model.capabilities.canExportRoutes}
            />
        ),
    },
    {
        key: "route-management-create-fab",
        order: 20,
        isVisible: (m) => m.capabilities.canCreateRoute && !m.isOffline,
        render: () => <CreateRouteFabBlock />,
    },
];
